import React from 'react';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { FilterButtons } from 'apps/sigma2-ui/src/filters-v2/components/FilterButtons';
import { FilterBoxError } from 'apps/sigma2-ui/src/filters-v2/components/FilterBoxError';
import cn from 'classnames';
import { FilterSingleItem } from 'apps/sigma2-ui/src/filters-v2/components/FilterSingleItem';
import { IconChevronRightSvg } from 'apps/sigma2-ui/src/components/svg/IconChevronRight';
import { TypographyTextColorVariant } from 'apps/sigma2-ui/src/components/ui/Typography';
import { TranslatedContent } from 'libs/end-to-end/src/translation-content';

export type FilterMenuDualPanelBottomProps =
  | 'none'
  | FilterMenuDualPanelBottomWithContentProps;

export interface FilterMenuDualPanelBottomWithContentProps {
  /**
   * The error message to display or null if no error
   */
  errorTranslatedContent: TranslatedContent | null;

  /**
   * Callback when the cancel button is clicked
   */
  onCancel: () => void;

  /**
   * Callback when the apply button is clicked
   */
  onApply: () => void;

  /**
   * Whether or not the apply button is disabled
   */
  applyDisabled: boolean;

  /**
   * The count of applied filters
   */
  appliedCount: number;
}

export interface FilterMenuDualPanelItemProps {
  /**
   * The key of the item
   */
  key: string;

  /**
   * The item translated content
   */
  translatedContent: TranslatedContent;

  /**
   * The callback for when the item is clicked
   */
  onClick: () => void;

  /**
   * Whether the item is selected or not
   */
  selected: boolean;

  /**
   * The content to render in the right panel
   */
  content: React.ReactNode;

  color?: TypographyTextColorVariant;

  /**
   * The item value (should be unique) for tracking purposes.
   */
  itemValue: string;
}

export interface FilterMenuDualPanelProps {
  /**
   * The items to show in the left panel, with additional information such as the content to render in the right panel
   */
  items: FilterMenuDualPanelItemProps[];

  /**
   * The bottom props for the dual panel (or none if no bottom to show)
   */
  bottomProps: FilterMenuDualPanelBottomProps;
}

export const FilterMenuDualPanel = ({
  items,
  bottomProps,
}: FilterMenuDualPanelProps) => {
  const selectedItem = items.find((item) => item.selected);

  return (
    <Spacing
      direction={'vertical'}
      size={'none'}
      className={'w-[480px] h-[316px] bg-white rounded-lg shadow-4px'}
    >
      <Spacing direction={'horizontal'} size={''} className={'flex-1 p-3'}>
        <div
          className={'w-[166px]'}
          data-testid={'filter-menu-dual-panel-left'}
        >
          <div className={cn('p-3 relative h-full')}>
            <div
              className={'absolute left-0 top-0 w-full h-full overflow-hidden'}
            >
              <div className={'h-full overflow-y-auto relative'}>
                <Spacing direction={'vertical'} size={'none'}>
                  {items.map((item) => (
                    <FilterMenuDualPanelItem {...item} key={item.key} />
                  ))}
                </Spacing>
              </div>
            </div>
          </div>
        </div>
        <div className={'w-[1px] bg-gray-200'} />
        <div className={'flex-1'} data-testid={'filter-dual-panel-right'}>
          <div className={cn('p-3 relative h-full')}>
            <div
              className={'absolute left-0 top-0 w-full h-full overflow-hidden'}
            >
              <div className={'h-full overflow-y-auto relative'}>
                {selectedItem && (
                  <div key={selectedItem.key}>{selectedItem.content}</div>
                )}
              </div>
            </div>
          </div>
        </div>
      </Spacing>
      {bottomProps !== 'none' && (
        <Spacing direction={'vertical'} size={''} className={'p-3'}>
          {bottomProps.errorTranslatedContent && (
            <FilterBoxError
              errorTranslatedContent={bottomProps.errorTranslatedContent}
            />
          )}
          <FilterButtons
            onCancel={bottomProps.onCancel}
            onApply={bottomProps.onApply}
            appliedCount={bottomProps.appliedCount}
            applyDisabled={bottomProps.applyDisabled}
          />
        </Spacing>
      )}
    </Spacing>
  );
};

const FilterMenuDualPanelItem = ({
  selected,
  onClick,
  translatedContent,
  color,
  itemValue,
}: FilterMenuDualPanelItemProps) => {
  return (
    <FilterSingleItem
      onClick={onClick}
      selected={selected}
      translatedContent={translatedContent}
      icon={IconChevronRightSvg}
      itemValue={itemValue}
      color={color ?? null}
    />
  );
};
